import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Activity,
  Brain,
  Plug,
  Shield,
  Key,
  ChevronRight,
  Loader2,
  Clock3,
  CheckCircle2,
  XCircle,
  Pencil,
  Users,
  Globe,
  Lock,
  Sparkles,
} from 'lucide-react';
import { getCurrentUserProfile, formatUserProfile } from '../utils/auth.ts';
import { useAI } from '../context/AIContext';
import { agentAPI, userAPI, communityAPI, customAgentAPI, gitAPI, gmailAPI, calendarAPI, instagramAPI, linkedinAPI, twitterAPI } from '../services/api.js';
import LyraApiKeyModal from './modals/LyraApiKeyModal.tsx';
import OnboardingModal from './modals/OnboardingModal.jsx';
import { AGENT_DOMAINS } from '../config/agentDomains.ts';

type IntegrationRow = {
  id: string;
  label: string;
  connected: boolean | null;
  detail: string | null;
};

function sectionCardClasses() {
  return 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm';
}

function formatTimeUntilExpiry(seconds: number) {
  if (seconds <= 0) return 'Expired';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${Math.max(1, mins)}m`;
}

const UserProfileDisplay: React.FC = () => {
  const navigate = useNavigate();
  const userProfile = getCurrentUserProfile();

  const { models, selectedMode, selectedModelId, disabledModels, engramApiKey, engramChatEnabled } = useAI() as any;

  const [domains, setDomains] = useState<string[]>([]);
  const [domainsSaving, setDomainsSaving] = useState(false);

  // ── Lyran identity ──────────────────────────────────────────────────────
  const [lyranName, setLyranName] = useState<string | null>(null);
  const [lyranAgentId, setLyranAgentId] = useState<string | null>(null);
  const [editingLyranName, setEditingLyranName] = useState(false);
  const [lyranNameDraft, setLyranNameDraft] = useState('');
  const [lyranNameTaken, setLyranNameTaken] = useState(false);
  const [lyranNameError, setLyranNameError] = useState<string | null>(null);
  const [lyranNameSaving, setLyranNameSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [isPublicSaving, setIsPublicSaving] = useState(false);
  const [followers, setFollowers] = useState<any[]>([]);
  const [followersLoading, setFollowersLoading] = useState(true);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [usageCost, setUsageCost] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrations, setIntegrations] = useState<IntegrationRow[]>([]);

  useEffect(() => {
    let mounted = true;
    async function loadPreferences() {
      try {
        const res = await userAPI.getUserPreferences();
        if (!mounted) return;
        const ds = res?.preferences?.domains;
        setDomains(Array.isArray(ds) ? ds.map((d: string) => String(d).toLowerCase()) : []);
        setLyranName(res?.preferences?.lyranName || null);
        setLyranAgentId(res?.preferences?.lyranAgentId || null);
      } catch {
        if (!mounted) return;
        setDomains([]);
      }
    }
    loadPreferences();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadCommunityProfile() {
      setFollowersLoading(true);
      try {
        const res: any = await communityAPI.getMyProfile();
        if (!mounted) return;
        setIsPublic(res?.profile?.isPublic !== false);
      } catch {
        if (!mounted) return;
      }
      try {
        const myId = userProfile?.id;
        if (!myId) return;
        const res: any = await communityAPI.getFollowers(myId);
        if (!mounted) return;
        setFollowers(Array.isArray(res?.followers) ? res.followers : []);
      } catch {
        if (!mounted) return;
        setFollowers([]);
      } finally {
        if (mounted) setFollowersLoading(false);
      }
    }
    loadCommunityProfile();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadUsage() {
      setUsageLoading(true);
      setUsageError(null);
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_API_URL ?? '';

        const [statsRes, costRes] = await Promise.allSettled([
          agentAPI.getStats(),
          fetch(`${apiBase}/api/usage/cost-summary`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          }).then(async (r) => (r.ok ? r.json() : null)),
        ]);

        if (!mounted) return;

        if (statsRes.status === 'fulfilled') {
          setUsageStats(statsRes.value?.stats || null);
        }

        if (costRes.status === 'fulfilled') {
          setUsageCost(costRes.value?.success ? costRes.value : null);
        }
      } catch (err: any) {
        if (!mounted) return;
        setUsageError(err?.message || 'Failed to load usage data');
      } finally {
        if (mounted) setUsageLoading(false);
      }
    }

    loadUsage();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadIntegrations() {
      setIntegrationsLoading(true);
      const checks = await Promise.allSettled([
        gitAPI.githubStatus(),
        gitAPI.gitlabStatus(),
        gmailAPI.status(),
        calendarAPI.status(),
        twitterAPI.credentialsStatus(),
        instagramAPI.status(),
        linkedinAPI.status(),
      ]);

      if (!mounted) return;

      const rows: IntegrationRow[] = [
        {
          id: 'github',
          label: 'GitHub',
          connected: checks[0].status === 'fulfilled' ? !!checks[0].value?.connected : null,
          detail: checks[0].status === 'fulfilled' ? (checks[0].value?.username ? `@${checks[0].value.username}` : null) : null,
        },
        {
          id: 'gitlab',
          label: 'GitLab',
          connected: checks[1].status === 'fulfilled' ? !!checks[1].value?.connected : null,
          detail: checks[1].status === 'fulfilled' ? (checks[1].value?.username ? `@${checks[1].value.username}` : null) : null,
        },
        {
          id: 'gmail',
          label: 'Gmail',
          connected: checks[2].status === 'fulfilled' ? !!checks[2].value?.connected : null,
          detail: checks[2].status === 'fulfilled' ? (checks[2].value?.email || null) : null,
        },
        {
          id: 'calendar',
          label: 'Google Calendar',
          connected: checks[3].status === 'fulfilled' ? !!checks[3].value?.connected : null,
          detail: checks[3].status === 'fulfilled' ? (checks[3].value?.email || null) : null,
        },
        {
          id: 'twitter',
          label: 'Twitter / X',
          connected: checks[4].status === 'fulfilled' ? !!checks[4].value?.configured : null,
          detail: checks[4].status === 'fulfilled' ? (checks[4].value?.username ? `@${checks[4].value.username}` : null) : null,
        },
        {
          id: 'instagram',
          label: 'Instagram',
          connected: checks[5].status === 'fulfilled' ? !!checks[5].value?.connected : null,
          detail: null,
        },
        {
          id: 'linkedin',
          label: 'LinkedIn',
          connected: checks[6].status === 'fulfilled' ? !!checks[6].value?.connected : null,
          detail: checks[6].status === 'fulfilled' ? (checks[6].value?.linkedinEmail || checks[6].value?.linkedinName || null) : null,
        },
      ];

      setIntegrations(rows);
      setIntegrationsLoading(false);
    }

    loadIntegrations();
    return () => {
      mounted = false;
    };
  }, []);

  const formattedProfile = useMemo(() => {
    if (!userProfile) return null;
    return formatUserProfile(userProfile);
  }, [userProfile]);

  const selectedModel = useMemo(() => {
    if (!Array.isArray(models)) return null;
    return models.find((m: any) => m.id === selectedModelId) || null;
  }, [models, selectedModelId]);

  const enabledModelsCount = useMemo(() => {
    if (!Array.isArray(models)) return null;
    if (!(disabledModels instanceof Set)) return models.length;
    return models.filter((m: any) => !disabledModels.has(m.id)).length;
  }, [models, disabledModels]);

  const connectedCount = useMemo(() => integrations.filter(i => i.connected === true).length, [integrations]);

  const refreshDomains = async () => {
    try {
      const res = await userAPI.getUserPreferences();
      const ds = res?.preferences?.domains;
      setDomains(Array.isArray(ds) ? ds.map((d: string) => String(d).toLowerCase()) : []);
      window.dispatchEvent(new CustomEvent('lyra:domains-changed', { detail: { domains: ds || [] } }));
    } catch {
      // keep current domains on failure
    }
  };

  const startEditingLyranName = () => {
    setLyranNameDraft(lyranName || '');
    setLyranNameTaken(false);
    setLyranNameError(null);
    setEditingLyranName(true);
  };

  const checkLyranNameAvailability = async (val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return;
    try {
      const resp = await userAPI.checkLyranName(trimmed);
      setLyranNameTaken(!!resp?.exists && !resp?.ownerIsCurrent);
    } catch {
      // Availability check failures are UI-only — don't block on them.
    }
  };

  const saveLyranName = async () => {
    const trimmed = lyranNameDraft.trim();
    if (!trimmed || !lyranAgentId || lyranNameSaving) return;
    if (trimmed === lyranName) {
      setEditingLyranName(false);
      return;
    }
    setLyranNameSaving(true);
    setLyranNameError(null);
    try {
      const res: any = await customAgentAPI.update(lyranAgentId, { name: trimmed });
      setLyranName(res?.agent?.name || trimmed);
      setEditingLyranName(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Could not update your Lyran\'s name';
      setLyranNameError(msg);
      if (err?.response?.status === 409) setLyranNameTaken(true);
    } finally {
      setLyranNameSaving(false);
    }
  };

  const togglePrivacy = async () => {
    if (isPublicSaving) return;
    const next = !isPublic;
    setIsPublicSaving(true);
    setIsPublic(next);
    try {
      await communityAPI.updateMyProfile({ isPublic: next });
    } catch {
      setIsPublic(!next);
    } finally {
      setIsPublicSaving(false);
    }
  };

  const toggleDomain = async (domainId: string) => {
    const next = domains.includes(domainId)
      ? domains.filter((d) => d !== domainId)
      : [...domains, domainId];

    setDomains(next);
    setDomainsSaving(true);
    try {
      await userAPI.updateUserPreferences({ domains: next });
      window.dispatchEvent(new CustomEvent('lyra:domains-changed', { detail: { domains: next } }));
    } catch {
      setDomains(domains);
    } finally {
      setDomainsSaving(false);
    }
  };

  if (!userProfile || !formattedProfile) {
    return (
      <div className={`${sectionCardClasses()} p-8 text-center`}>
        <User className="h-14 w-14 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No account session</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Please log in to access account settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="text-xs tracking-[0.08em] uppercase text-gray-500 dark:text-gray-400 font-semibold">Settings</div>
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white mt-1">Account</h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Manage your identity, preferences and Lyra account.</p>
      </div>

      <div className="space-y-4">
        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">1. Profile / Identity</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your authenticated account identity.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="h-14 w-14 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center shrink-0">
              {userProfile.profilePicture ? (
                <img src={userProfile.profilePicture} alt={userProfile.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{formattedProfile.initials}</span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{userProfile.name}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 mt-0.5 truncate">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{userProfile.email}</span>
              </div>
            </div>
          </div>
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">2. Lyran Identity</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your personal AI agent's name, visibility, and followers.</p>
            </div>
            <div className="h-8 w-8 rounded-lg bg-[color:var(--app-primary-soft)] text-[color:var(--app-primary-mid)] flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
          </div>

          {/* Lyran name */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Lyran name</div>
            {editingLyranName ? (
              <div>
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={lyranNameDraft}
                    onChange={(e) => {
                      setLyranNameDraft(e.target.value);
                      if (lyranNameTaken) setLyranNameTaken(false);
                      if (lyranNameError) setLyranNameError(null);
                    }}
                    onBlur={(e) => checkLyranNameAvailability(e.target.value)}
                    className="flex-1 min-w-0 text-sm px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={saveLyranName}
                    disabled={lyranNameSaving || !lyranNameDraft.trim() || lyranNameTaken}
                    className="text-xs font-medium px-3 py-1.5 rounded-md bg-[color:var(--app-primary-mid)] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[color:var(--app-primary-deep)] inline-flex items-center gap-1"
                  >
                    {lyranNameSaving && <Loader2 className="h-3 w-3 animate-spin" />}
                    Save
                  </button>
                  <button
                    onClick={() => setEditingLyranName(false)}
                    className="text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
                {lyranNameTaken && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1.5">Duplicate Lyran! not allowed.. choose another name.</div>
                )}
                {lyranNameError && !lyranNameTaken && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1.5">{lyranNameError}</div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{lyranName || '—'}</span>
                <button
                  onClick={startEditingLyranName}
                  disabled={!lyranAgentId}
                  className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)] inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
              </div>
            )}
          </div>

          {/* Privacy */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mt-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                {isPublic ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                {isPublic ? 'Public' : 'Private'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {isPublic ? 'Your Lyran is listed in the Teams Lyrans directory.' : 'Your Lyran is hidden from the Teams Lyrans directory.'}
              </div>
            </div>
            <button
              onClick={togglePrivacy}
              disabled={isPublicSaving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${isPublic ? 'bg-[color:var(--app-primary-mid)]' : 'bg-gray-300 dark:bg-gray-600'} disabled:opacity-60`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isPublic ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Followers */}
          <div className="mt-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Followers ({followers.length})
            </div>
            {followersLoading ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
            ) : followers.length === 0 ? (
              <div className="text-xs text-gray-500 dark:text-gray-400">No followers yet.</div>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {followers.map((f) => (
                  <div key={f.userId} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5">
                    <div className="h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-semibold text-gray-700 dark:text-gray-200 shrink-0">
                      {(f.name || f.lyran_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <span className="text-xs text-gray-800 dark:text-gray-200 truncate max-w-[140px]">{f.name || f.lyran_name || 'Unknown'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">3. Lyra Usage</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Real runtime and model usage available for your account.</p>
            </div>
            <button
              onClick={() => navigate('/ai-console')}
              className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)] inline-flex items-center gap-1"
            >
              Open AI Console <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {usageLoading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
            </div>
          ) : usageError ? (
            <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
              Usage data is currently unavailable.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {typeof usageStats?.total === 'number' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">AI runs</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{usageStats.total}</div>
                </div>
              )}
              {typeof usageStats?.runsToday === 'number' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Runs today</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{usageStats.runsToday}</div>
                </div>
              )}
              {usageStats?.successRate !== undefined && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Success rate</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{usageStats.successRate}%</div>
                </div>
              )}
              {usageCost?.summary && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Monthly model tokens</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {usageCost.summary.totalTokensUsed ?? 0}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">4. AI & Model Preferences</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Current model mode and memory settings used by Lyra.</p>
            </div>
            <button
              onClick={() => navigate('/ai-console')}
              className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)] inline-flex items-center gap-1"
            >
              Manage <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Mode</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">{selectedMode || 'auto'}</div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Default model</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{selectedModel?.name || 'Auto selection'}</div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Enabled models</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {enabledModelsCount === null ? 'Not available' : `${enabledModelsCount} / ${Array.isArray(models) ? models.length : 0}`}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Engram memory in chat</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{engramChatEnabled ? 'Enabled' : 'Disabled'}</div>
            </div>
          </div>

          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Engram key: {engramApiKey ? 'Connected' : 'Not connected'}
          </div>
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">5. Lyra API Key</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Create and manage your personal Lyra API key for CLI and MCP access.</p>
            </div>
            <button
              onClick={() => setShowApiKeyModal(true)}
              className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)] inline-flex items-center gap-1"
            >
              Manage Lyra API Key <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-[color:var(--app-primary-soft)] text-[color:var(--app-primary-mid)] flex items-center justify-center">
              <Key className="h-4 w-4" />
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-200">
              Use this key with <span className="font-mono">agent-lyra init --api-key</span> to sync your web-created agents into <span className="font-mono">.lyra</span>.
            </div>
          </div>
        </section>

        <section id="domains" className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">6. Domain Folders</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Choose which domain folders appear in the left menu. Changes apply instantly.</p>
            </div>
            <div className="flex items-center gap-3">
              {domainsSaving && (
                <div className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {AGENT_DOMAINS.map((d) => {
              const checked = domains.includes(d.id);
              return (
                <label key={d.id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center justify-between gap-3 cursor-pointer">
                  <span className="text-sm text-gray-800 dark:text-gray-200">{d.label}</span>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDomain(d.id)}
                    className="h-4 w-4"
                  />
                </label>
              );
            })}
          </div>

          {domains.length === 0 && (
            <div className="mt-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
              No domains selected. Left menu will show a prompt to pick domains.
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            These checkboxes only control the left menu. To add ready-made agents for a
            domain to your Agents list, use <button onClick={() => setShowAgentPicker(true)} className="text-[color:var(--app-primary-mid)] hover:underline font-medium">Edit agent picks</button>.
          </p>
        </section>

        {showAgentPicker && (
          <OnboardingModal
            mode="preferences"
            onComplete={() => { setShowAgentPicker(false); refreshDomains(); }}
            onSkip={() => setShowAgentPicker(false)}
          />
        )}

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">7. Connected Services</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Live status from your configured integrations.</p>
            </div>
            <button
              onClick={() => navigate('/integrations')}
              className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)] inline-flex items-center gap-1"
            >
              Manage Integrations <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {integrationsLoading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading integration status…
            </div>
          ) : (
            <>
              <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {connectedCount} connected of {integrations.length}
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                {integrations.map((item) => (
                  <div key={item.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.detail || 'No additional details'}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.connected === true && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      {item.connected === false && <XCircle className="h-4 w-4 text-gray-400" />}
                      {item.connected === null && <Clock3 className="h-4 w-4 text-amber-500" />}
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          item.connected === true
                            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : item.connected === false
                              ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                              : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                      >
                        {item.connected === true ? 'Connected' : item.connected === false ? 'Not connected' : 'Unknown'}
                      </span>
                      <button
                        onClick={() => navigate('/integrations')}
                        className="text-xs font-medium text-[color:var(--app-primary-mid)] hover:text-[color:var(--app-primary-deep)]"
                      >
                        {item.connected ? 'Manage' : 'Connect'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5`}>
          <div className="mb-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">8. Security</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Session and account security information that is currently available.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Current session</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                Expires in {formatTimeUntilExpiry(formattedProfile.timeUntilExpiry)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Expiry: {formattedProfile.expiresAt}</div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">Authentication method</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">Not exposed by current API</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">No reliable provider field in client session token.</div>
            </div>
          </div>
        </section>

        <section className={`${sectionCardClasses()} p-4 md:p-5 border-red-200 dark:border-red-800`}>
          <div className="flex items-start gap-3">
            <Shield className="h-4 w-4 text-red-600 mt-0.5" />
            <div>
              <h2 className="text-base font-semibold text-red-700 dark:text-red-300">Danger Zone</h2>
              <p className="text-xs text-red-600/90 dark:text-red-300/80 mt-1">
                Account deletion is not exposed by current backend routes, so no destructive action is shown here.
              </p>
            </div>
          </div>
        </section>
      </div>

      {showApiKeyModal && <LyraApiKeyModal onClose={() => setShowApiKeyModal(false)} />}
    </div>
  );
};

export default UserProfileDisplay;
